import { readAccessDeviceRegistry } from '@/api/core/access-device-registry';
import { canReadDomain, canReadProperties, coreQueryKey } from '@/api/core/authorization';
import {
  readReservations,
  reservationCommand,
  type Reservation,
  type ReservationStatus,
} from '@/api/core/lora-reservations';
import { readNativeCatalog, readNativeProperties } from '@/api/core/native-property-read';
import { isApiError } from '@/api/http/errors';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { isUuid } from '@/lib/validation';
import {
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  ListPage,
  PageHeader,
  type DataTableColumn,
} from '@mgrvtechgroup/admin-ds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Choice } from '../property/presentation/choice';
import { propertyRead, propertyRuntime, type PropertyRuntime } from '../property/property-queries';

const labels: Record<ReservationStatus, string> = {
  REQUESTED: '생성 요청됨',
  ACTIVE: '사용 중',
  DELETE_REQUESTED: '삭제 요청됨',
  DELETING_UNKNOWN: '삭제 결과 확인 필요',
  UNKNOWN: '처리 결과 확인 필요',
  FAILED: '실패',
  CANCELLED: '취소됨',
};
const results: Record<string, string> = {
  TARGET_MISMATCH: '기기 대상 불일치',
  RESIDENT_REVIEW: '입주자 확인 필요',
  VERIFIED: '확인 완료',
  FAILED: '처리 실패',
  UNKNOWN: '결과 확인 필요',
};
const conflicts: Record<string, string> = {
  RESIDENTS_PRESENT: '현재 입주자가 있어 취소할 수 없습니다. 강제 취소하려면 사유를 입력하세요.',
  RESULT_PENDING: '기기 응답을 확인하는 중입니다. 잠시 후(최대 60초) 다시 시도하세요.',
  FORCE_REQUIRED: '기기 상태가 예상과 달라 강제 취소(사유 필수)만 가능합니다.',
  REVISION_CONFLICT: '다른 변경이 먼저 반영됐습니다. 새로고침 후 다시 시도하세요.',
  INVALID_STATE: '현재 상태에서는 이 작업을 할 수 없습니다.',
};
export function LoraReservationsPage({ runtime = propertyRuntime }: { runtime?: PropertyRuntime }) {
  const { propertyId } = useParams();
  return (
    <ReservationsForProperty key={propertyId ?? ''} runtime={runtime} propertyId={propertyId} />
  );
}
function ReservationsForProperty({
  runtime,
  propertyId,
}: {
  runtime: PropertyRuntime;
  propertyId?: string;
}) {
  const session = useOperatorSession(runtime.session);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [propertyCursors, setPropertyCursors] = useState<(string | null)[]>([null]);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [selected, setSelected] = useState<{ row: Reservation; action: 'cancel' | 'reset' }>();
  const [force, setForce] = useState(false);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const current = (bit: number) => {
    const s = runtime.session.getSnapshot();
    return (
      s.generation === session.generation &&
      s.status === 'authorized' &&
      !!s.authorization &&
      !s.deniedDomains.includes('entitlement') &&
      canReadDomain(s.authorization, 'entitlement', bit)
    );
  };
  const allowed =
    session.status === 'authorized' &&
    !!session.authorization &&
    !session.deniedDomains.includes('entitlement') &&
    canReadDomain(session.authorization, 'entitlement', 1);
  const writable =
    session.status === 'authorized' &&
    !!session.authorization &&
    !session.deniedDomains.includes('entitlement') &&
    canReadDomain(session.authorization, 'entitlement', 4);
  const key = [
    ...coreQueryKey(runtime.apiNamespace, session.generation, 'lora-reservations'),
    propertyId,
    cursors.at(-1),
  ];
  const query = useQuery({
    queryKey: key,
    enabled: allowed && !!propertyId && isUuid(propertyId),
    queryFn: async ({ signal }) => {
      if (!current(1)) throw new Error('권한을 확인할 수 없습니다.');
      try {
        const data = await readReservations(
          runtime.client,
          {
            property_id: propertyId,
            cursor: cursors.at(-1),
          },
          signal,
        );
        if (!current(1)) throw new Error('권한을 확인할 수 없습니다.');
        return data;
      } catch (e) {
        if (isApiError(e) && e.status === 403 && !signal.aborted && current(1))
          runtime.session.reportDomainAccessDenied('entitlement', session.generation);
        throw e;
      }
    },
  });
  const propertyAllowed =
    session.status === 'authorized' &&
    !!session.authorization &&
    !session.deniedDomains.includes('property') &&
    canReadProperties(session.authorization);
  const propertyCurrent = () => {
    const s = runtime.session.getSnapshot();
    return (
      s.generation === session.generation &&
      s.status === 'authorized' &&
      !!s.authorization &&
      !s.deniedDomains.includes('property') &&
      canReadProperties(s.authorization)
    );
  };
  const propertyList = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, session.generation, 'reservation-properties'),
      propertyCursors.at(-1),
    ],
    enabled: propertyAllowed,
    retry: false,
    queryFn: ({ signal }) =>
      propertyRead(
        runtime,
        session.generation,
        () => readNativeProperties(runtime.client, propertyCursors.at(-1) ?? null, 'asc', signal),
        signal,
        propertyCurrent,
      ),
  });
  const names = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, session.generation, 'reservation-names'),
      propertyId,
      query.data?.items.map((row) => `${row.unit_id}:${row.device_id}`).join(','),
    ],
    enabled: propertyAllowed && !!propertyId && isUuid(propertyId) && !!query.data,
    retry: false,
    queryFn: async ({ signal }) =>
      propertyRead(
        runtime,
        session.generation,
        async () => {
          const slots: Record<string, string> = {};
          const devices: Record<string, string> = {};
          const missingSlots = new Set(query.data?.items.map((row) => row.unit_id));
          const missingDevices = new Set(query.data?.items.map((row) => row.device_id));
          let cursor: string | null = null;
          const visited = new Set<string>();
          let propertyName = '';
          do {
            const page = await readNativeCatalog(runtime.client, propertyId ?? '', cursor, signal);
            propertyName = `${page.data.property.name} · ${page.data.property.code}`;
            for (const room of page.data.rooms)
              for (const unit of room.units) {
                if (missingSlots.delete(unit.id))
                  slots[unit.id] = `${room.name || '객실명 없음'} · ${unit.name || '슬롯명 없음'}`;
              }
            cursor = page.next_cursor;
            if (cursor && visited.has(cursor))
              throw new Error('카탈로그 페이지를 확인할 수 없습니다.');
            if (cursor) visited.add(cursor);
          } while (cursor && missingSlots.size);
          cursor = null;
          visited.clear();
          while (missingDevices.size) {
            const page = await readAccessDeviceRegistry(
              runtime.client,
              { cursor: cursor ?? undefined, limit: 50 },
              signal,
            );
            for (const device of page.items) {
              if (missingDevices.delete(device.core_device_id)) {
                const name = device.device_name?.trim();
                devices[device.core_device_id] = name?.length ? name : '기기 이름 없음';
              }
            }
            cursor = page.next_cursor;
            if (!cursor) break;
            if (visited.has(cursor)) throw new Error('장비 페이지를 확인할 수 없습니다.');
            visited.add(cursor);
          }
          return { slots, devices, propertyName };
        },
        signal,
        propertyCurrent,
      ),
  });
  const run = async (row: Reservation, action: 'create' | 'cancel' | 'reset') => {
    if (
      !writable ||
      !current(4) ||
      busy ||
      (action !== 'create' && (row.id === null || row.revision === null))
    )
      return;
    if (action === 'cancel' && force && !reason.trim()) {
      setMessage('강제 취소 사유를 입력해 주세요.');
      return;
    }
    const generation = session.generation;
    setBusy(true);
    setMessage('');
    try {
      await reservationCommand(
        runtime.client,
        action,
        action === 'create'
          ? {
              command_id: crypto.randomUUID(),
              id: crypto.randomUUID(),
              unit_id: row.unit_id,
              device_id: row.device_id,
              expected_revision: 0,
            }
          : {
              command_id: crypto.randomUUID(),
              id: row.id ?? '',
              expected_revision: row.revision ?? 0,
              ...(action === 'cancel' ? { force, reason: force ? reason.trim() : null } : {}),
            },
      );
      if (!current(4)) return;
      setSelected(undefined);
      setForce(false);
      setReason('');
      setMessage(
        '요청을 접수했습니다. 기기에 전달·반영됐다는 뜻은 아니며, 처리 상태는 아래 표에서 확인하세요.',
      );
      await qc.invalidateQueries({
        queryKey: coreQueryKey(runtime.apiNamespace, generation, 'lora-reservations'),
      });
    } catch (e) {
      if (runtime.session.getSnapshot().generation !== generation) return;
      if (isApiError(e) && e.status === 403)
        runtime.session.reportDomainAccessDenied('entitlement', generation);
      if (isApiError(e) && e.status === 409) {
        setMessage(
          conflicts[e.code ?? ''] ?? '다른 변경과 충돌했습니다. 새로고침 후 다시 시도하세요',
        );
        if (e.code === 'FORCE_REQUIRED' && action === 'reset') {
          setForce(false);
          setReason('');
          setSelected({ row, action: 'cancel' });
        }
        if (e.code === 'REVISION_CONFLICT') {
          setSelected(undefined);
          await qc.invalidateQueries({
            queryKey: coreQueryKey(runtime.apiNamespace, generation, 'lora-reservations'),
          });
        }
      } else
        setMessage(
          isApiError(e) && e.status === 404
            ? '이 환경에서는 예약 관리가 비활성 상태입니다.'
            : '예약 요청을 처리하지 못했습니다.',
        );
    } finally {
      setBusy(false);
    }
  };
  if (session.status !== 'authorized') return null;
  return (
    <ListPage>
      <ShellHeader>
        <PageHeader title="슬롯 예약 관리" description="슬롯과 도어락의 예약 상태를 관리합니다." />
      </ShellHeader>
      <ListPage.Body className="space-y-4 p-4">
        <p className="caption text-muted-foreground">
          ‘생성 요청됨’·‘삭제 요청됨’은 요청이 접수만 된 상태입니다. 기기에 전달·반영됐다는 뜻이
          아니며, 반영이 확인되면 ‘사용 중’·‘취소됨’으로 바뀝니다.
        </p>
        {/* 확인창이 닫힌 뒤(직접 생성·재설정 후)에도 접수 결과와 실패 사유가 보이도록 목록 위에 둔다. */}
        {!selected && message && <p role="status">{message}</p>}
        {!allowed && <p role="alert">예약 조회 권한이 없습니다.</p>}
        {!propertyAllowed && <p role="alert">지점과 장비 이름을 조회할 권한이 없습니다.</p>}
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span>지점</span>
            <Choice
              label="지점"
              value={propertyId ?? 'none'}
              disabled={!propertyAllowed || propertyList.isPending}
              options={{
                none: '지점 선택',
                ...(propertyId ? { [propertyId]: '선택한 지점' } : {}),
                ...Object.fromEntries(
                  (propertyList.data?.data ?? []).map((p) => [p.id, `${p.name} · ${p.code}`]),
                ),
                ...(propertyId && names.data?.propertyName
                  ? { [propertyId]: names.data.propertyName }
                  : {}),
              }}
              onChange={(value) => {
                if (value === 'none') void navigate('/access/reservations');
                else void navigate(`/access/reservations/${value}`);
              }}
            />
          </label>
          {propertyCursors.length > 1 && (
            <Button
              variant="outline"
              onClick={() => {
                setPropertyCursors(propertyCursors.slice(0, -1));
              }}
            >
              이전 지점
            </Button>
          )}
          {propertyList.data?.next_cursor && (
            <Button
              variant="outline"
              onClick={() => {
                setPropertyCursors([...propertyCursors, propertyList.data.next_cursor ?? null]);
              }}
            >
              다음 지점
            </Button>
          )}
        </div>
        {propertyList.isError && <p role="alert">지점 목록을 불러오지 못했습니다.</p>}
        {!propertyId && <p>지점을 선택해 주세요.</p>}
        {propertyId && !isUuid(propertyId) && <p role="alert">지점을 확인할 수 없습니다.</p>}
        {names.isError && <p role="alert">객실과 기기 이름을 불러오지 못했습니다.</p>}
        {message && !selected && <p role="alert">{message}</p>}
        {allowed && propertyId && isUuid(propertyId) && query.isPending && (
          <p role="status">예약을 조회하고 있습니다.</p>
        )}
        {query.isError && (
          <p role="alert">
            {isApiError(query.error) && query.error.status === 404
              ? '이 환경에서는 예약 관리가 비활성 상태입니다.'
              : '예약을 불러오지 못했습니다.'}
          </p>
        )}
        {query.data && (
          <>
            <div
              className="min-w-0 overflow-x-auto"
              role="region"
              aria-label="예약 목록"
              tabIndex={0}
            >
              <div className="min-w-[60rem]">
                <DataTable
                  data={query.data.items}
                  rowKey={(row) => `${row.unit_id}-${row.device_id}`}
                  empty={{ title: '조회된 슬롯이 없습니다.' }}
                  columns={
                    [
                      {
                        key: 'unit_id',
                        header: '슬롯',
                        cell: (row: Reservation) => (
                          <span>
                            {names.data?.slots[row.unit_id] ??
                              (names.isFetching ? '이름 조회 중' : '객실·슬롯 이름 확인 불가')}
                          </span>
                        ),
                      },
                      {
                        key: 'device_id',
                        header: '기기',
                        cell: (row: Reservation) => (
                          <span>
                            {names.data?.devices[row.device_id] ??
                              (names.isFetching ? '이름 조회 중' : '기기 이름 확인 불가')}
                          </span>
                        ),
                      },
                      {
                        key: 'reservation_id',
                        header: '예약 번호',
                        cell: (row: Reservation) => row.reservation_id ?? '—',
                      },
                      {
                        key: 'status',
                        header: '상태',
                        cell: (row: Reservation) => (row.status ? labels[row.status] : '예약 없음'),
                      },
                      {
                        key: 'period',
                        header: '기간',
                        cell: (row: Reservation) =>
                          row.reserv_from && row.reserv_until
                            ? `${new Date(row.reserv_from).toLocaleString('ko-KR')} ~ ${new Date(row.reserv_until).toLocaleString('ko-KR')}`
                            : '—',
                      },
                      {
                        key: 'last_result',
                        header: '마지막 결과',
                        cell: (row: Reservation) =>
                          row.last_result ? (results[row.last_result] ?? '운영 확인 필요') : '—',
                      },
                      {
                        key: 'actions',
                        header: '관리',
                        cell: (row: Reservation) =>
                          writable &&
                          (row.id === null ? (
                            <Button
                              disabled={busy}
                              onClick={() => {
                                void run(row, 'create');
                              }}
                            >
                              예약 생성
                            </Button>
                          ) : (
                            <div className="flex gap-2">
                              <Button
                                disabled={busy}
                                variant="outline"
                                onClick={() => {
                                  setMessage('');
                                  setSelected({ row, action: 'reset' });
                                }}
                              >
                                {row.status === 'FAILED' ? '예약 생성' : '재설정'}
                              </Button>
                              <Button
                                disabled={busy}
                                variant="outline"
                                onClick={() => {
                                  setMessage('');
                                  setForce(false);
                                  setReason('');
                                  setSelected({ row, action: 'cancel' });
                                }}
                              >
                                취소
                              </Button>
                            </div>
                          )),
                      },
                    ] satisfies DataTableColumn<Reservation>[]
                  }
                />
              </div>
            </div>
            <nav className="flex gap-2" aria-label="예약 페이지">
              {cursors.length > 1 && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setCursors(cursors.slice(0, -1));
                  }}
                >
                  이전
                </Button>
              )}
              {query.data.next_cursor && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setCursors([...cursors, query.data.next_cursor ?? undefined]);
                  }}
                >
                  다음
                </Button>
              )}
            </nav>
          </>
        )}
        <Dialog
          open={!!selected}
          onOpenChange={(open) => {
            if (!open && !busy) {
              setSelected(undefined);
              setMessage('');
            }
          }}
        >
          {selected && (
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {selected.action === 'cancel' ? '예약 취소' : '예약 재설정'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {selected.action === 'reset' ? (
                  <p>
                    재설정을 요청하면 현재 입주자에게 새 랜덤 PIN 발급이 요청됩니다. 요청은 접수만
                    되며, 기기에 반영됐는지는 아래 표의 상태가 바뀐 뒤 확인됩니다.
                  </p>
                ) : (
                  <>
                    <label className="flex gap-2">
                      <input
                        type="checkbox"
                        checked={force}
                        onChange={(e) => {
                          setForce(e.target.checked);
                        }}
                      />
                      입주자가 있어도 강제 취소
                    </label>
                    {force && (
                      <label>
                        강제 취소 사유
                        <Input
                          maxLength={500}
                          value={reason}
                          onChange={(e) => {
                            setReason(e.target.value);
                          }}
                        />
                      </label>
                    )}
                  </>
                )}
                {message && <p role="alert">{message}</p>}
                <div className="flex gap-2">
                  <Button disabled={busy} onClick={() => void run(selected.row, selected.action)}>
                    {selected.action === 'cancel' ? '예약 취소' : '재설정 확인'}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      setSelected(undefined);
                      setMessage('');
                    }}
                  >
                    닫기
                  </Button>
                </div>
              </div>
            </DialogContent>
          )}
        </Dialog>
      </ListPage.Body>
    </ListPage>
  );
}
